import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFRelatedPartyMonitor = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('quickcheck');
    
    // Quick Check Calculator (Tests 758-770)
    const [totalFundAssets, setTotalFundAssets] = useState(smsfState.smsfData?.balance || 2000000);
    const [relatedPartyLoan, setRelatedPartyLoan] = useState(0);
    const [relatedPartyProperty, setRelatedPartyProperty] = useState(0);
    const [relatedPartyBusiness, setRelatedPartyBusiness] = useState(0);
    const [hasCalculated, setHasCalculated] = useState(false);
    
    // Transaction Monitor
    const [transactions, setTransactions] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    
    const [newTransaction, setNewTransaction] = useState({
        type: 'loan',
        date: '',
        relatedParty: '',
        relationship: 'member',
        description: '',
        amount: 0,
        interestRate: 0,
        terms: '',
        marketValue: 0,
        status: 'active',
        compliance: 'compliant'
    });

    // Related party transaction types
    const transactionTypes = {
        loan: { name: 'Loan to Related Party', icon: '💰', allowed: false, risk: 'Prohibited' },
        loanFrom: { name: 'Loan from Related Party (LRBA)', icon: '🏦', allowed: true, risk: 'High' },
        asset_purchase: { name: 'Asset Purchase from Related Party', icon: '🏠', allowed: true, risk: 'High' },
        asset_sale: { name: 'Asset Sale to Related Party', icon: '📤', allowed: true, risk: 'High' },
        service: { name: 'Services from Related Party', icon: '🔧', allowed: true, risk: 'Medium' },
        in_house: { name: 'In-House Asset', icon: '🏢', allowed: true, risk: 'High' },
        financial: { name: 'Financial Assistance', icon: '💵', allowed: false, risk: 'Prohibited' }
    };

    // Relationship types
    const relationshipTypes = [
        { value: 'member', label: 'SMSF Member' },
        { value: 'relative', label: 'Member Relative' },
        { value: 'employer', label: 'Employer' },
        { value: 'business', label: 'Related Business' },
        { value: 'trust', label: 'Related Trust' },
        { value: 'company', label: 'Related Company' }
    ];

    // Quick Check Calculations
    const totalInHouseAssets = relatedPartyLoan + relatedPartyProperty + relatedPartyBusiness;
    const inHouseAssetRatio = totalFundAssets > 0 ? (totalInHouseAssets / totalFundAssets) * 100 : 0;
    const isCompliant = inHouseAssetRatio <= 5;
    const excessAmount = isCompliant ? 0 : totalInHouseAssets - (totalFundAssets * 0.05);
    
    const handleCalculate = () => {
        setHasCalculated(true);
    };

    // Transaction Monitor Functions
    const handleAddTransaction = () => {
        if (newTransaction.description && newTransaction.amount > 0) {
            if (editingId) {
                setTransactions(transactions.map(item => 
                    item.id === editingId ? { ...newTransaction, id: editingId } : item
                ));
                setEditingId(null);
            } else {
                setTransactions([...transactions, { ...newTransaction, id: Date.now() }]);
            }
            setNewTransaction({
                type: 'loan',
                date: '',
                relatedParty: '',
                relationship: 'member',
                description: '',
                amount: 0,
                interestRate: 0,
                terms: '',
                marketValue: 0,
                status: 'active',
                compliance: 'compliant'
            });
            setShowAddForm(false);
        }
    };

    const handleEditTransaction = (item) => {
        setNewTransaction({ ...item });
        setEditingId(item.id);
        setShowAddForm(true);
    };

    const handleRemoveTransaction = (id) => {
        setTransactions(transactions.filter(t => t.id !== id));
    };

    // Quick Check Tab
    const renderQuickCheck = () => (
        <div className="space-y-6">
            {/* Input Section */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Fund Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Total Fund Assets ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={totalFundAssets}
                            onChange={(e) => setTotalFundAssets(Number(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="2000000"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Related Party Loan ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={relatedPartyLoan}
                            onChange={(e) => setRelatedPartyLoan(Number(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="50000"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Related Party Property ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={relatedPartyProperty}
                            onChange={(e) => setRelatedPartyProperty(Number(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="30000"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Related Party Business ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={relatedPartyBusiness}
                            onChange={(e) => setRelatedPartyBusiness(Number(e.target.value) || 0)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="20000"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                            <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-700">Total In-House Assets:</span>
                                <span className="text-lg font-bold text-blue-700">
                                    ${totalInHouseAssets.toLocaleString()}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={handleCalculate}
                        className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                    >
                        Calculate Compliance
                    </button>
                </div>
            </div>
            
            {/* Results Section */}
            {hasCalculated && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Compliance Results</h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        {/* In-House Asset Ratio */}
                        <div className={`p-4 rounded-lg border-2 ${
                            isCompliant ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                        }`}>
                            <div className="text-sm text-gray-600 mb-1">In-House Asset Ratio</div>
                            <div className={`text-3xl font-bold ${
                                isCompliant ? 'text-green-700' : 'text-red-700'
                            }`}>
                                {inHouseAssetRatio.toFixed(2)}%
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                ${totalInHouseAssets.toLocaleString()} / ${totalFundAssets.toLocaleString()}
                            </div>
                        </div>
                        
                        {/* 5% Limit */}
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-sm text-gray-600 mb-1">5% Limit Threshold</div>
                            <div className="text-3xl font-bold text-blue-700">
                                ${(totalFundAssets * 0.05).toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Maximum allowed in-house assets</div>
                        </div>
                        
                        {/* Compliance Status */}
                        <div className={`p-4 rounded-lg border-2 ${
                            isCompliant ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                        }`}>
                            <div className="text-sm text-gray-600 mb-1">Compliance Status</div>
                            <div className={`text-3xl font-bold ${
                                isCompliant ? 'text-green-700' : 'text-red-700'
                            }`}>
                                {isCompliant ? '✅ Compliant' : '❌ Breach'}
                            </div>
                            <div className="text-xs text-gray-500 mt-1">
                                {isCompliant ? 'Within 5% limit' : 'Exceeds 5% limit'}
                            </div>
                        </div>
                    </div>
                    
                    {/* Excess Amount */}
                    {!isCompliant && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-md mb-4">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-red-800">Excess Amount:</span>
                                <span className="text-xl font-bold text-red-700">
                                    ${excessAmount.toLocaleString()}
                                </span>
                            </div>
                            <p className="text-xs text-red-600 mt-2">
                                ATO regulations require in-house assets to be reduced by this amount to achieve compliance
                            </p>
                        </div>
                    )}
                    
                    {/* Remediation Timeline */}
                    {!isCompliant && (
                        <div className="p-4 bg-orange-50 border border-orange-200 rounded-md mb-4">
                            <h4 className="font-semibold text-orange-800 mb-2">⏰ Remediation Timeline</h4>
                            <p className="text-sm text-orange-700">
                                <strong>ATO regulations require this breach to be rectified within 12 months</strong> from the end of the financial year in which the breach occurred.
                                Failure to remediate may result in penalties and loss of SMSF tax concessions.
                            </p>
                        </div>
                    )}
                    
                    {/* Remediation Options */}
                    {!isCompliant && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                            <h4 className="font-semibold text-blue-800 mb-3">💡 Remediation Options</h4>
                            <ul className="space-y-2 text-sm text-blue-700">
                                <li className="flex items-start">
                                    <span className="mr-2">1.</span>
                                    <span><strong>Sell the in-house asset</strong> to an unrelated party at market value</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="mr-2">2.</span>
                                    <span><strong>Repay loans</strong> from related parties to reduce in-house asset value</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="mr-2">3.</span>
                                    <span><strong>Increase total fund assets</strong> through contributions (dilutes the percentage)</span>
                                </li>
                                <li className="flex items-start">
                                    <span className="mr-2">4.</span>
                                    <span><strong>Convert to arm's length arrangement</strong> if the asset can be restructured to no longer be in-house</span>
                                </li>
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );

    // Transaction Monitor Tab
    const renderTransactions = () => (
        <div className="space-y-6">
            {/* Add Transaction Button */}
            <div className="flex justify-end">
                <button
                    onClick={() => setShowAddForm(!showAddForm)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                    {showAddForm ? 'Cancel' : '+ Add Transaction'}
                </button>
            </div>

            {/* Add/Edit Form */}
            {showAddForm && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit Transaction' : 'Add Related Party Transaction'}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Type</label>
                            <select
                                value={newTransaction.type}
                                onChange={(e) => setNewTransaction({ ...newTransaction, type: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                {Object.entries(transactionTypes).map(([key, type]) => (
                                    <option key={key} value={key}>{type.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                            <input
                                type="date"
                                value={newTransaction.date}
                                onChange={(e) => setNewTransaction({ ...newTransaction, date: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Related Party Name</label>
                            <input
                                type="text"
                                value={newTransaction.relatedParty}
                                onChange={(e) => setNewTransaction({ ...newTransaction, relatedParty: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="Name of related party"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Relationship</label>
                            <select
                                value={newTransaction.relationship}
                                onChange={(e) => setNewTransaction({ ...newTransaction, relationship: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                                {relationshipTypes.map(rel => (
                                    <option key={rel.value} value={rel.value}>{rel.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                            <input
                                type="text"
                                value={newTransaction.description}
                                onChange={(e) => setNewTransaction({ ...newTransaction, description: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="Transaction details"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Transaction Amount ($)</label>
                            <input
                                {...getNumericInputProps()}
                                value={newTransaction.amount}
                                onChange={(e) => setNewTransaction({ ...newTransaction, amount: Number(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Market Value ($)</label>
                            <input
                                {...getNumericInputProps()}
                                value={newTransaction.marketValue}
                                onChange={(e) => setNewTransaction({ ...newTransaction, marketValue: Number(e.target.value) })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            />
                        </div>
                        {(newTransaction.type === 'loanFrom' || newTransaction.type === 'loan') && (
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Interest Rate (%)</label>
                                <input
                                    {...getNumericInputProps()}
                                    step="0.1"
                                    value={newTransaction.interestRate}
                                    onChange={(e) => setNewTransaction({ ...newTransaction, interestRate: Number(e.target.value) })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                        )}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Terms/Documentation</label>
                            <input
                                type="text"
                                value={newTransaction.terms}
                                onChange={(e) => setNewTransaction({ ...newTransaction, terms: e.target.value })}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                placeholder="Written agreement details"
                            />
                        </div>
                    </div>
                    <div className="mt-4 flex justify-end space-x-2">
                        <button
                            onClick={() => {
                                setShowAddForm(false);
                                setEditingId(null);
                                setNewTransaction({
                                    type: 'loan',
                                    date: '',
                                    relatedParty: '',
                                    relationship: 'member',
                                    description: '',
                                    amount: 0,
                                    interestRate: 0,
                                    terms: '',
                                    marketValue: 0,
                                    status: 'active',
                                    compliance: 'compliant'
                                });
                            }}
                            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAddTransaction}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                            {editingId ? 'Update Transaction' : 'Add Transaction'}
                        </button>
                    </div>
                </div>
            )}

            {/* Transactions List */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Related Party Transactions</h3>
                {transactions.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <p>No transactions recorded yet</p>
                        <p className="text-sm mt-2">Click "Add Transaction" to start tracking</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {transactions.map((item) => (
                            <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                                <div className="flex justify-between items-start mb-2">
                                    <div className="flex items-center">
                                        <span className="text-2xl mr-3">{transactionTypes[item.type].icon}</span>
                                        <div>
                                            <h4 className="font-semibold text-gray-800">{item.description}</h4>
                                            <p className="text-sm text-gray-600">{transactionTypes[item.type].name}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleEditTransaction(item)}
                                            className="px-3 py-1 text-blue-600 hover:bg-blue-50 rounded-md text-sm border border-blue-300"
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => handleRemoveTransaction(item.id)}
                                            className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-md text-sm border border-red-300"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                    <div>
                                        <div className="text-xs text-gray-600">Related Party</div>
                                        <div className="font-medium">{item.relatedParty || 'Not specified'}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-600">Amount</div>
                                        <div className="font-medium">${item.amount.toLocaleString()}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-600">Date</div>
                                        <div className="font-medium">{item.date || 'Not specified'}</div>
                                    </div>
                                    <div>
                                        <div className="text-xs text-gray-600">Status</div>
                                        <div className={`font-medium ${
                                            transactionTypes[item.type].allowed ? 'text-orange-600' : 'text-red-600'
                                        }`}>
                                            {transactionTypes[item.type].risk}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );

    // Compliance Tab
    const renderCompliance = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Related Party Rules</h3>
                <div className="space-y-4">
                    <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                        <h4 className="font-semibold text-red-800 mb-2">❌ Prohibited Transactions</h4>
                        <ul className="text-sm text-red-700 space-y-1">
                            <li>• Loans TO related parties (members, relatives, etc.)</li>
                            <li>• Providing financial assistance to members</li>
                            <li>• Acquiring assets from related parties (except limited exceptions)</li>
                        </ul>
                    </div>
                    
                    <div className="p-4 bg-orange-50 border border-orange-200 rounded-md">
                        <h4 className="font-semibold text-orange-800 mb-2">⚠️ Allowed with Restrictions</h4>
                        <ul className="text-sm text-orange-700 space-y-1">
                            <li>• Limited Recourse Borrowing Arrangements (LRBA)</li>
                            <li>• Business real property from related parties</li>
                            <li>• Listed securities at market value</li>
                            <li>• In-house assets (max 5% of fund value)</li>
                        </ul>
                    </div>
                    
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                        <h4 className="font-semibold text-blue-800 mb-2">📋 Compliance Requirements</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                            <li>• All transactions must be at arm's length</li>
                            <li>• Written agreements required</li>
                            <li>• Market valuations for assets</li>
                            <li>• Annual compliance checks</li>
                            <li>• In-house assets ≤ 5% of total fund assets</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Related Party Monitor</h2>
                <p className="text-sm text-gray-600">In-House Asset & Transaction Compliance</p>
            </div>

            {/* Tab Navigation */}
            <div className="bg-white p-4 rounded-lg shadow-md">
                <div className="flex space-x-2">
                    <button
                        onClick={() => setActiveTab('quickcheck')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'quickcheck' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        Quick Check
                    </button>
                    <button
                        onClick={() => setActiveTab('transactions')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'transactions' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        Transactions
                    </button>
                    <button
                        onClick={() => setActiveTab('compliance')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'compliance' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
                        }`}
                    >
                        Compliance Rules
                    </button>
                </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'quickcheck' && renderQuickCheck()}
            {activeTab === 'transactions' && renderTransactions()}
            {activeTab === 'compliance' && renderCompliance()}
        </div>
    );
};

SMSFRelatedPartyMonitor.propTypes = {
    smsfState: PropTypes.object,
    setSmsfState: PropTypes.func
};

export default SMSFRelatedPartyMonitor;
